import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

interface Invoice {
  id: string;
  amount: number;
  description: string | null;
  incurred_on: string;
}

interface LaborEntry {
  id: string;
  hours: number;
  rate: number;
  worked_on: string;
  description: string | null;
}

interface Props {
  projectId: string;
  initialChargedTotal: number | null;
  initialLaborHours: number | null;
  initialLaborRate: number | null;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const parseNum = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const ProjectProfitPanel = ({
  projectId,
  initialChargedTotal,
  initialLaborHours,
  initialLaborRate,
}: Props) => {
  const [charged, setCharged] = useState<string>(initialChargedTotal?.toString() ?? "");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [laborEntries, setLaborEntries] = useState<LaborEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [newAmount, setNewAmount] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));

  // Default the new labor rate to the last-used rate for this project (or the legacy project rate)
  const defaultRate =
    laborEntries[0]?.rate?.toString() ?? initialLaborRate?.toString() ?? "";
  const [newLaborHours, setNewLaborHours] = useState("");
  const [newLaborRate, setNewLaborRate] = useState<string>(initialLaborRate?.toString() ?? "");
  const [newLaborDesc, setNewLaborDesc] = useState("");
  const [newLaborDate, setNewLaborDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [invRes, labRes] = await Promise.all([
        (supabase as any)
          .from("project_invoices")
          .select("*")
          .eq("project_id", projectId)
          .order("incurred_on", { ascending: false }),
        (supabase as any)
          .from("project_labor_entries")
          .select("*")
          .eq("project_id", projectId)
          .order("worked_on", { ascending: false }),
      ]);
      if (invRes.error) console.error(invRes.error);
      if (labRes.error) console.error(labRes.error);
      setInvoices(invRes.data || []);
      setLaborEntries(labRes.data || []);
      // Seed rate from most recent entry if we have one
      if (labRes.data?.[0]?.rate != null) {
        setNewLaborRate(String(labRes.data[0].rate));
      }
      setLoading(false);
    })();
  }, [projectId]);

  const invoiceTotal = useMemo(
    () => invoices.reduce((s, i) => s + Number(i.amount || 0), 0),
    [invoices]
  );
  const laborTotal = useMemo(
    () => laborEntries.reduce((s, e) => s + Number(e.hours || 0) * Number(e.rate || 0), 0),
    [laborEntries]
  );
  const totalHours = useMemo(
    () => laborEntries.reduce((s, e) => s + Number(e.hours || 0), 0),
    [laborEntries]
  );
  const chargedNum = Number(charged) || 0;
  const profit = chargedNum - invoiceTotal - laborTotal;

  const saveProject = async (patch: Record<string, number | null>) => {
    const { error } = await supabase.from("projects").update(patch).eq("id", projectId);
    if (error) toast.error("Failed to save");
  };

  const handleAddInvoice = async () => {
    const amt = parseNum(newAmount);
    if (amt === null) {
      toast.error("Enter an amount");
      return;
    }
    const { data, error } = await (supabase as any)
      .from("project_invoices")
      .insert({
        project_id: projectId,
        amount: amt,
        description: newDesc.trim() || null,
        incurred_on: newDate,
      })
      .select()
      .single();
    if (error) {
      toast.error("Failed to add invoice");
      return;
    }
    setInvoices((prev) => [data as Invoice, ...prev]);
    setNewAmount("");
    setNewDesc("");
  };

  const handleDeleteInvoice = async (id: string) => {
    const { error } = await (supabase as any).from("project_invoices").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete");
      return;
    }
    setInvoices((prev) => prev.filter((i) => i.id !== id));
  };

  const handleAddLabor = async () => {
    const h = parseNum(newLaborHours);
    const r = parseNum(newLaborRate);
    if (h === null || h <= 0) {
      toast.error("Enter hours");
      return;
    }
    if (r === null) {
      toast.error("Enter a rate");
      return;
    }
    const { data, error } = await (supabase as any)
      .from("project_labor_entries")
      .insert({
        project_id: projectId,
        hours: h,
        rate: r,
        worked_on: newLaborDate,
        description: newLaborDesc.trim() || null,
      })
      .select()
      .single();
    if (error) {
      toast.error("Failed to add labor entry");
      return;
    }
    setLaborEntries((prev) => [data as LaborEntry, ...prev]);
    setNewLaborHours("");
    setNewLaborDesc("");
  };

  const handleDeleteLabor = async (id: string) => {
    const { error } = await (supabase as any).from("project_labor_entries").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete");
      return;
    }
    setLaborEntries((prev) => prev.filter((e) => e.id !== id));
  };

  return (
    <div className="border border-border p-4 bg-background space-y-5">
      <h4 className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Profit Tracking</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Charged to client ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={charged}
            onChange={(e) => setCharged(e.target.value)}
            onBlur={() => saveProject({ charged_total: parseNum(charged) })}
            className="mt-1"
          />
        </div>
      </div>

      {/* Labor entries */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Labor Entries</h5>
          <span className="text-xs text-muted-foreground">
            {totalHours}h · Total: {fmt(laborTotal)}
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : laborEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No labor logged yet.</p>
        ) : (
          <div className="space-y-1">
            {laborEntries.map((e) => {
              const sub = Number(e.hours) * Number(e.rate);
              return (
                <div key={e.id} className="flex items-center gap-2 text-sm py-1 border-b border-border/50">
                  <span className="text-xs text-muted-foreground w-24 shrink-0">{e.worked_on}</span>
                  <span className="w-20 shrink-0">{Number(e.hours)}h</span>
                  <span className="w-24 shrink-0 text-muted-foreground">@ {fmt(Number(e.rate))}</span>
                  <span className="font-medium w-24 shrink-0">{fmt(sub)}</span>
                  <span className="flex-1 text-muted-foreground truncate">{e.description}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteLabor(e.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input type="date" value={newLaborDate} onChange={(e) => setNewLaborDate(e.target.value)} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Hours</Label>
            <Input
              type="number"
              step="0.25"
              value={newLaborHours}
              onChange={(e) => setNewLaborHours(e.target.value)}
              className="mt-1"
              placeholder="0"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Rate ($/hr)</Label>
            <Input
              type="number"
              step="0.01"
              value={newLaborRate}
              onChange={(e) => setNewLaborRate(e.target.value)}
              className="mt-1"
              placeholder="0.00"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-muted-foreground">Note</Label>
            <Input
              value={newLaborDesc}
              onChange={(e) => setNewLaborDesc(e.target.value)}
              className="mt-1"
              placeholder="Week of..."
            />
          </div>
          <div className="col-span-2">
            <Button size="sm" onClick={handleAddLabor} className="w-full">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Invoices</h5>
          <span className="text-xs text-muted-foreground">Total: {fmt(invoiceTotal)}</span>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No invoices yet.</p>
        ) : (
          <div className="space-y-1">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 text-sm py-1 border-b border-border/50">
                <span className="text-xs text-muted-foreground w-24 shrink-0">{inv.incurred_on}</span>
                <span className="font-medium w-24 shrink-0">{fmt(Number(inv.amount))}</span>
                <span className="flex-1 text-muted-foreground truncate">{inv.description}</span>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteInvoice(inv.id)}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              className="mt-1"
              placeholder="0.00"
            />
          </div>
          <div className="col-span-4">
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className="mt-1"
              placeholder="Vendor / material"
            />
          </div>
          <div className="col-span-2">
            <Button size="sm" onClick={handleAddInvoice} className="w-full">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Charged</span>
          <span>{fmt(chargedNum)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">− Invoices</span>
          <span>{fmt(invoiceTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">− Labor ({totalHours}h)</span>
          <span>{fmt(laborTotal)}</span>
        </div>
        <div className={`flex justify-between font-semibold text-base pt-1 border-t border-border ${profit >= 0 ? "text-green-700" : "text-destructive"}`}>
          <span>Profit</span>
          <span>
            {fmt(profit)}
            {chargedNum > 0 && (
              <span className="ml-2 text-sm font-normal opacity-80">
                ({((profit / chargedNum) * 100).toFixed(1)}%)
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProjectProfitPanel;
